import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './SchoolLogin.css';

function UserLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is already logged in
    const userData = localStorage.getItem('userRoleData');
    if (userData) {
      navigate('/school/dashboard');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Find user by username or email
      const { data: users, error: fetchError } = await supabase
        .from('user_roles')
        .select('*')
        .or(`username.eq.${formData.usernameOrEmail},email.eq.${formData.usernameOrEmail}`)
        .eq('is_active', true);

      if (fetchError) {
        throw new Error('Failed to authenticate. Please try again.');
      }

      if (!users || users.length === 0) {
        setError('Invalid username/email or password');
        setLoading(false);
        return;
      }

      // Check password (simple comparison - in production, use hashing)
      const user = users[0];
      if (user.password !== formData.password) {
        setError('Invalid username or password');
        setLoading(false);
        return;
      }

      // Get school data if school_id exists
      let schoolData = null;
      if (user.school_id) {
        const { data: school, error: schoolError } = await supabase
          .from('schools')
          .select('*')
          .eq('id', user.school_id)
          .single();

        if (!schoolError && school) {
          schoolData = school;
        }
      }

      // Store user data in localStorage
      localStorage.setItem('userRoleData', JSON.stringify({
        ...user,
        schoolData: schoolData
      }));
      localStorage.setItem('userRoleAuthenticated', 'true');

      // Navigate to school dashboard
      navigate('/school/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="school-login-container">
      <div className="school-login-card">
        <div className="school-login-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy App Logo" 
            className="school-login-logo"
          />
          <h1>User Login</h1>
          <p>Access your dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit} className="school-login-form">
          <div className="form-group">
            <label htmlFor="usernameOrEmail">Username or Email</label>
            <div className="input-wrapper">
              <input
                type="text"
                id="usernameOrEmail"
                name="usernameOrEmail"
                value={formData.usernameOrEmail}
                onChange={handleInputChange}
                placeholder="Enter your username or email"
                className={error ? 'error' : ''}
                style={{ backgroundColor: '#F9FAFB', outline: 'none' }}
                onFocus={(e) => e.target.style.backgroundColor = '#F9FAFB'}
                required
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className={error ? 'error' : ''}
                style={{ backgroundColor: '#F9FAFB', outline: 'none' }}
                onFocus={(e) => e.target.style.backgroundColor = '#F9FAFB'}
                required
                disabled={loading}
              />
            </div>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                    <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                    <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                  </circle>
                </svg>
                Signing in...
              </>
            ) : 'Sign In'}
          </button>
        </form>

        <div className="help-text">
          Need help? Contact your administrator
        </div>
      </div>
    </div>
  );
}

export default UserLogin;
